import { create } from 'zustand';
import apiClient from '../lib/api';

// ---------------------------------------------------------------- types

export interface ManagerRef {
  id: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
  isPrimary?: boolean;
}

export interface ReportingLine {
  id: string;                 // employee id
  employeeCode: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: string;
  designation?: string;
  status?: string;
  managers: ManagerRef[];     // all reporting managers (primary first)
}

export interface ManagerCandidate {
  id: string;
  employeeCode: string;
  firstName: string;
  lastName: string;
  department?: string;
  designation?: string;
}

// ---------------------------------------------------------------- normalize

function normalizeManagerRef(raw: any): ManagerRef {
  return {
    id: raw.id,
    firstName: raw.firstName ?? raw.first_name ?? '',
    lastName: raw.lastName ?? raw.last_name ?? '',
    employeeCode: raw.employeeCode ?? raw.employee_code ?? '',
    isPrimary: raw.isPrimary ?? raw.is_primary ?? false,
  };
}

function normalizeLine(raw: any): ReportingLine {
  const managers = Array.isArray(raw.managers) ? raw.managers.map(normalizeManagerRef) : [];
  return {
    id: raw.id,
    employeeCode: raw.employee_code ?? raw.employeeCode ?? '',
    firstName: raw.first_name ?? raw.firstName ?? '',
    lastName: raw.last_name ?? raw.lastName ?? '',
    email: raw.email ?? '',
    department: raw.department ?? '',
    designation: raw.designation ?? '',
    status: raw.status ?? 'active',
    managers,
  };
}

function normalizeCandidate(raw: any): ManagerCandidate {
  return {
    id: raw.id,
    employeeCode: raw.employee_code ?? raw.employeeCode ?? '',
    firstName: raw.first_name ?? raw.firstName ?? '',
    lastName: raw.last_name ?? raw.lastName ?? '',
    department: raw.department ?? '',
    designation: raw.designation ?? '',
  };
}

// ---------------------------------------------------------------- store

interface ReportingManagerState {
  lines: ReportingLine[];
  candidates: ManagerCandidate[];
  isLoading: boolean;
  error: string | null;

  fetchLines: () => Promise<void>;
  fetchCandidates: () => Promise<void>;
  setManagers: (employeeId: string, managerIds: string[]) => Promise<void>;
  clearManagers: (employeeId: string) => Promise<void>;
}

export const useReportingManagerStore = create<ReportingManagerState>((set, get) => ({
  lines: [],
  candidates: [],
  isLoading: false,
  error: null,

  fetchLines: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.get('/people/reporting-managers');
      const raw = res.data?.data ?? [];
      set({ lines: raw.map(normalizeLine), isLoading: false });
    } catch (e: any) {
      set({ error: e.response?.data?.message || 'Failed to load reporting lines', isLoading: false });
    }
  },

  fetchCandidates: async () => {
    try {
      const res = await apiClient.get('/people/reporting-managers/candidates');
      const raw = res.data?.data ?? [];
      set({ candidates: raw.map(normalizeCandidate) });
    } catch (e: any) {
      console.error('Failed to load manager candidates', e);
    }
  },

  setManagers: async (employeeId: string, managerIds: string[]) => {
    const res = await apiClient.put(`/people/reporting-managers/${employeeId}`, { managerIds });
    const updated = normalizeLine(res.data?.data ?? res.data);
    set({ lines: get().lines.map((l) => (l.id === employeeId ? updated : l)) });
  },

  clearManagers: async (employeeId: string) => {
    await apiClient.delete(`/people/reporting-managers/${employeeId}`);
    set({ lines: get().lines.map((l) => (l.id === employeeId ? { ...l, managers: [] } : l)) });
  },
}));
